import { Component, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { LoadingPanelService } from '../../shared/loading-panel/loading-panel.service';
import { CurrentUserService } from '../../user/services';
import { BatchingConfiguration } from '../models/batchconfiguration';
import { ToastService, ToastType } from '../../shared/toast/toast.service';
import { DeveloperService } from '../services/developer.service';
import { DocumentTypeEntity } from '../../distribution/models';
import { ScheduleItem } from '../../shared/scheduling/models';
import { DocumentTypeDeveloper } from '../models/document-type';
import { ScheduleItemComponent } from 'src/app/shared/scheduling';
import { GenericModalService, ModalSettingsMessage } from 'src/app/shared/generic-modal/generic-modal.service';
import { SvgIconComponent } from '../../shared/icons/svg-icon.component';
import { PermissionRequiredDirective } from '../../shared/authentication/permission-required.directive';
import { ChangeLogLinkComponent } from '../../shared/change-log/change-log-link/change-log-link.component';
import { ChangeLogLinkEntityDirective } from '../../shared/change-log/change-log-link-entity/change-log-link-entity.directive';
import { DxDataGridModule, DxDropDownBoxModule } from 'devextreme-angular';
import { BizDataGridDirective } from '../../shared/grid/biz-grid.directive';
import { DxoEditingModule, DxoPopupModule, DxoFormModule, DxiItemModule, DxiColumnModule, DxiValidationRuleModule, DxiButtonModule, DxoPagingModule, DxoScrollingModule, DxoSelectionModule } from 'devextreme-angular/ui/nested';
import { BizUserColumnDirective } from '../../shared/grid/biz-grid-user.directive';
import { BizDateColumnDirective } from '../../shared/grid/biz-grid-date.directive';
import { DxTemplateModule } from 'devextreme-angular/core';
import { ScheduleItemComponent as ScheduleItemComponent_1 } from '../../shared/scheduling/schedule-item.component';

import { DataFieldGroupContainerComponent } from '../../shared/datafields/datafield-group-container.component';
import { BizGridFiltervalues } from 'src/app/shared/grid/biz-grid-filtervalues';

@Component({
    selector: 'app-developer-batching-configurations',
    templateUrl: './developer-batching-configurations.component.html',
    styleUrls: ['./developer-batching-configurations.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [SvgIconComponent, PermissionRequiredDirective, ChangeLogLinkComponent, ChangeLogLinkEntityDirective, DxDataGridModule, BizDataGridDirective, DxoEditingModule, DxoPopupModule, DxoFormModule, DxiItemModule, DxiColumnModule, DxiValidationRuleModule, BizUserColumnDirective, BizDateColumnDirective, DxiButtonModule, DxTemplateModule, DxDropDownBoxModule, DxoPagingModule, DxoScrollingModule, DxoSelectionModule, ScheduleItemComponent_1, DataFieldGroupContainerComponent]
})
export class DeveloperBatchingConfigurationsComponent implements OnInit {
    hasCreatePerm = false;
    hasEditPerm = false;
    hasDeletePerm = false;
    hasShowLogPerm = false;

    batchingConfigurations: BatchingConfiguration[] = [];
    docTypes: DocumentTypeDeveloper[] = [];
    documentTypeNone: DocumentTypeDeveloper = { Id: 0, Name: 'None', Description: 'Not filtered on document type' } as DocumentTypeDeveloper;
    selectedDocType: DocumentTypeDeveloper;

    @ViewChild('scheduleTemplate')
    scheduleTemplate: ScheduleItemComponent;

    constructor(
        private currentUserService: CurrentUserService,
        private loadingPanelService: LoadingPanelService,
        private developerService: DeveloperService,
        private toastService: ToastService,
        private modalService: GenericModalService,
        public bizGridFiltervalues: BizGridFiltervalues) {
    }

    async ngOnInit() {
        this.hasCreatePerm = this.currentUserService.hasPermission('Developer_Action_BatchingConfiguration_Create');
        this.hasEditPerm = this.currentUserService.hasPermission('Developer_Action_BatchingConfiguration_Edit');
        this.hasDeletePerm = this.currentUserService.hasPermission('Developer_Action_BatchingConfiguration_Delete');
        this.hasShowLogPerm = this.currentUserService.hasPermission('Developer_Action_ShowLog');

        this.refresh();
    }

    async refresh() {
        try {
            this.loadingPanelService.showLoadingPanelDirect('Loading batching configurations...');
            [
                this.docTypes,
                this.batchingConfigurations
            ] = await Promise.all([
                this.developerService.getDocumentTypes(),
                this.developerService.getBatchingConfigurations()
            ]);

            this.docTypes = [this.documentTypeNone, ...this.docTypes];
        }
        finally {
            this.loadingPanelService.hideLoadingPanel();
        }
    }

    displayExprDocumentType(e: any) {
        const item = e.value as DocumentTypeEntity;
        return item?.Id ? item.Name : null;
    }

    displayExprSchedule(e: any) {
        const item = e.value as ScheduleItem;
        const multi = item?.Triggers?.length > 1;
        return item?.Triggers?.map(n => multi ? `[${n.StartTime}: ${n.Weekdays}]` : `${n.StartTime}: ${n.Weekdays}`).join(' ');
    }

    async onInitNewRow(e: any): Promise<void> {
        const newSchedule = new ScheduleItem();
        newSchedule.StartDate = new Date('2023-01-01T00:00:00');
        const newConfig = new BatchingConfiguration();
        newConfig.NoOfDocuments = 1000;
        newConfig.ScheduleItem = newSchedule;
        newConfig.BatchingKey = '';
        newConfig.DocumentType = this.documentTypeNone;
        newConfig.MinutesSinceFirstDocument = null;
        newConfig.MinutesSinceLatestDocument = null;
        newConfig.IsDeleted = false;
        e.data = newConfig;
    }

    async saveBatchingConfiguration(entity: BatchingConfiguration, e: any): Promise<BatchingConfiguration> {
        try {
            if (!isNaN(e.newData?.DocumentType)) {
                if (e.newData.DocumentType > 0) {
                    entity.DocumentType = this.docTypes.find(element => element.Id === e.newData.DocumentType);
                }
                else {
                    entity.DocumentType = null;
                }
            }
            const savedItem = await this.developerService.saveBatchingConfiguration(entity);
            this.toastService.showToast('Batching configuration saved...', ToastType.success);
            return savedItem;
        }
        catch (err) {
            console.error(err);
            this.toastService.showToast('Failed to save batching configuration...', ToastType.error);
        }
        this.refresh();
        return undefined;
    }

    onRowValidating(e) {
        const missingFields: string[] = [];

        if (e.newData.Name?.length <= 0) {
            missingFields.push('Must contain name.)');
        }

        if (e.newData.NoOfDocuments <= 0) {
            missingFields.push('Number of documents must be greater than zero.)');
        }

        // Check if the schedule is invalid and add to missing fields if necessary
        if (this.scheduleTemplate !== undefined && this.scheduleTemplate !== null) {
            let invalidTriggers = this.scheduleTemplate.triggerItems.filter(x => x.weekdayPicker.selectedWeekdays.length === 0);
            if (invalidTriggers.length > 0) {
                missingFields.push('Schedule Configuration (Invalid schedule.)');
            }
        }

        if (missingFields.length > 0) {
            // Construct an error message from the missingFields
            const errorText = `Validation Error: ${missingFields.join(' - ')}`;
            e.errorText = errorText;
            e.isValid = false; // Set validation flag to false
        }
    }

    async onRowInserted(e: any): Promise<void> {
        if (!isNaN(e.data.DocumentType)) {
            e.data.DocumentType = this.docTypes.find(element => element.Id === e.data.DocumentType);
        }
        const item = e.data as BatchingConfiguration;
        const savedItem = await this.saveBatchingConfiguration(item, e);
        if (savedItem) {
            item.Id = savedItem.Id;
        }
        this.refresh();
    }

    async onEditCanceled(e: any): Promise<void> {
        this.refresh();
    }

    async onRowUpdated(e: any): Promise<void> {
        if (!isNaN(e.key.DocumentType)) {
            e.key.DocumentType = this.docTypes.find(element => element.Id === e.key.DocumentType);
        }
        let item = e.key as BatchingConfiguration;
        item = Object.assign({}, item, e.newData); // Clone instance and set updated data.
        await this.saveBatchingConfiguration(item, e);
        this.refresh();
    }

    async onRowRemoving(e: any): Promise<void> {
        let item = e.key as BatchingConfiguration;
        item.IsDeleted = true;
        item = Object.assign({}, item, e.newData); // Clone instance and set updated data.
        await this.saveBatchingConfiguration(item, e);
        this.refresh();
    }

    // For use with grid in drop down template
    onSelectionChanged(selectedRowKeys, cellInfo, dropDownBoxComponent): void {
        cellInfo.setValue(selectedRowKeys[0]);
        if (selectedRowKeys.length > 0) {
            dropDownBoxComponent.close();
        }
    }

    scheduleItemChange($event, cellInfo): void {
        cellInfo.setValue($event);
    }
}
